import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckoutFlow } from '../components/CheckoutFlow';
import { Globe, TrendingUp, Mic, Shield, Zap, Server, Lock, RefreshCw, Cpu, Database, ArrowRight, CheckCircle2, Search, Loader2 } from 'lucide-react';
interface HostingProps {
  setActiveSection?: (val: string) => void;
}
export function Hosting({
  setActiveSection
}: HostingProps) {
  const [domainQuery, setDomainQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<null | {
    domain: string;
    available: boolean;
    price: string;
  }>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutInitialStep, setCheckoutInitialStep] = useState<'search' | 'hosting'>('search');
  const [selectedDomainForCheckout, setSelectedDomainForCheckout] = useState<string | undefined>(undefined);
  const [selectedHostingForCheckout, setSelectedHostingForCheckout] = useState<{
    name: string;
    price: number;
  } | undefined>(undefined);
  const startCheckout = (step: 'search' | 'hosting', domain?: string, hosting?: {
    name: string;
    price: number;
  }) => {
    setCheckoutInitialStep(step);
    setSelectedDomainForCheckout(domain);
    setSelectedHostingForCheckout(hosting);
    setShowCheckout(true);
  };
  const handleDomainSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!domainQuery.trim()) return;
    setIsSearching(true);
    setSearchResult(null);
    // Simulate domain search
    setTimeout(() => {
      setIsSearching(false);
      setSearchResult({
        domain: domainQuery.includes('.') ? domainQuery : `${domainQuery}.com`,
        available: Math.random() > 0.3,
        price: '14.99'
      });
    }, 1500);
  };
  return <div className="min-h-screen bg-[#0D0D0D] text-white" data-id="element-345">
      <div className="pt-48 pb-32 px-6 max-w-7xl mx-auto" data-id="element-346">
        {/* Hero Section */}
        <section className="text-center mb-32" data-id="element-347">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-['Archivo_Black'] text-4xl md:text-6xl uppercase mb-6 bg-gradient-to-r from-[#FFD700] to-[#FF0000] bg-clip-text text-transparent" data-id="element-348">
            Pro Hosting
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-400 max-w-2xl mx-auto mb-16" data-id="element-349">
            Powered by Niishcloud enterprise infrastructure. Built for massive
            traffic spikes and elite performance.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-350">
            {[{
            title: '99.9% Uptime',
            icon: Globe,
            desc: 'Guaranteed availability for your global audience.'
          }, {
            title: 'Unlimited Bandwidth',
            icon: TrendingUp,
            desc: 'Scale without limits, no matter how viral you go.'
          }, {
            title: '24/7 Support',
            icon: Mic,
            desc: 'Expert technical assistance whenever you need it.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2 + i * 0.1
          }} className="p-10 bg-[#1A1A1A] rounded-2xl border border-white/5 hover:border-[#FFD700]/30 transition-all group" data-id="element-351">
                <item.icon className="text-[#FFD700] mx-auto mb-6 group-hover:scale-110 transition-transform" size={40} data-id="element-352" />
                <h3 className="font-bold uppercase text-xl mb-4" data-id="element-353">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-500" data-id="element-354">{item.desc}</p>
              </motion.div>)}
          </div>
        </section>

        {/* Domain Search Section */}
        <section className="mb-32" data-id="element-355">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] p-8 md:p-12 rounded-3xl border border-white/10 shadow-2xl relative overflow-hidden" data-id="element-356">
            <div className="relative z-10 text-center mb-10" data-id="element-357">
              <h3 className="font-['Archivo_Black'] text-2xl md:text-3xl uppercase mb-4" data-id="element-358">
                Secure Your Identity
              </h3>
              <p className="text-gray-400" data-id="element-359">
                Find the perfect custom domain for your brand empire.
              </p>
            </div>

            <form onSubmit={handleDomainSearch} className="relative z-10 flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto" data-id="element-360">
              <div className="flex-1 relative" data-id="element-361">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} data-id="element-362" />
                <input type="text" value={domainQuery} onChange={e => setDomainQuery(e.target.value)} placeholder="Enter your brand name..." className="w-full bg-black/50 border border-white/10 rounded-xl py-4 pl-12 pr-4 focus:outline-none focus:border-[#FFD700] transition-colors font-bold" data-id="element-363" />
              </div>
              <button disabled={isSearching} className="bg-[#FFD700] text-black px-8 py-4 rounded-xl font-bold uppercase tracking-widest hover:bg-white transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-id="element-364">
                {isSearching ? <Loader2 className="animate-spin" size={20} data-id="element-365" /> : 'Search'}
              </button>
            </form>

            {searchResult && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mt-8 max-w-2xl mx-auto p-6 bg-black/40 rounded-2xl border border-white/5 flex items-center justify-between" data-id="element-366">
                <div data-id="element-367">
                  <p className="text-lg font-bold" data-id="element-368">{searchResult.domain}</p>
                  <p className={`text-xs uppercase font-bold tracking-widest ${searchResult.available ? 'text-emerald-500' : 'text-red-500'}`} data-id="element-369">
                    {searchResult.available ? 'Available Now' : 'Already Taken'}
                  </p>
                </div>
                {searchResult.available && <div className="flex items-center gap-6" data-id="element-370">
                    <span className="text-xl font-['Archivo_Black']" data-id="element-371">
                      ${searchResult.price}
                      <span className="text-xs text-gray-500 font-sans" data-id="element-372">
                        /yr
                      </span>
                    </span>
                    <button onClick={() => startCheckout('search', searchResult.domain)} className="bg-white text-black px-6 py-2 rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-[#FFD700] transition-colors" data-id="element-373">
                      Claim
                    </button>
                  </div>}
              </motion.div>}

            <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#FFD700]/5 blur-[100px] rounded-full" data-id="element-374" />
          </div>
        </section>

        {/* Infrastructure Section */}
        <section className="mb-32" data-id="element-375">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-376">
            <div className="space-y-8" data-id="element-377">
              <span className="text-[#FFD700] font-bold uppercase tracking-[0.3em] text-xs" data-id="element-378">
                The Niishcloud Edge
              </span>
              <h2 className="font-['Archivo_Black'] text-3xl md:text-5xl uppercase leading-tight" data-id="element-379">
                Enterprise-Grade <br data-id="element-380" />
                <span className="text-white/50" data-id="element-381">Infrastructure</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed" data-id="element-382">
                We don't use shared hosting. Every HipHopServers site runs on
                dedicated, bare-metal resources optimized for high-fidelity
                media and streaming.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-383">
                {[{
                title: 'NVMe Storage',
                icon: Database,
                desc: '10x faster than SSD'
              }, {
                title: 'DDR5 RAM',
                icon: Cpu,
                desc: 'Ultra-low latency'
              }, {
                title: 'Dedicated IP',
                icon: Server,
                desc: 'Better SEO & Email'
              }, {
                title: 'Auto-Scaling',
                icon: Zap,
                desc: 'Handles traffic spikes'
              }].map((feat, i) => <div key={i} className="flex items-start gap-4" data-id="element-384">
                    <div className="p-2 bg-[#FFD700]/10 rounded-lg" data-id="element-385">
                      <feat.icon className="text-[#FFD700]" size={20} data-id="element-386" />
                    </div>
                    <div data-id="element-387">
                      <h4 className="font-bold uppercase text-sm" data-id="element-388">
                        {feat.title}
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-389">{feat.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="relative" data-id="element-390">
              <div className="aspect-square bg-gradient-to-br from-[#FFD700]/20 to-red-600/10 rounded-3xl border border-white/10 overflow-hidden flex items-center justify-center p-12" data-id="element-391">
                <div className="relative w-full h-full border border-[#FFD700]/20 rounded-full animate-[spin_20s_linear_infinite] flex items-center justify-center" data-id="element-392">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-[#FFD700] rounded-full shadow-[0_0_20px_#FFD700]" data-id="element-393" />
                  <div className="w-2/3 h-2/3 border border-white/10 rounded-full flex items-center justify-center" data-id="element-394">
                    <Server size={64} className="text-[#FFD700] animate-pulse" data-id="element-395" />
                  </div>
                </div>
              </div>
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#FFD700]/10 blur-[50px] rounded-full" data-id="element-396" />
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-red-600/10 blur-[50px] rounded-full" data-id="element-397" />
            </div>
          </div>
        </section>

        {/* Hosting Plans Section */}
        <section className="mb-32" data-id="element-398">
          <div className="text-center mb-16" data-id="element-399">
            <h2 className="font-['Archivo_Black'] text-3xl md:text-5xl uppercase mb-4" data-id="element-400">
              Hosting Plans
            </h2>
            <p className="text-gray-500 uppercase font-bold tracking-widest" data-id="element-401">
              Scalable power for every stage of your career
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-402">
            {[{
            name: 'Starter Node',
            price: 29,
            desc: 'Perfect for emerging artists and link hubs.',
            features: ['10GB NVMe Storage', '1TB Bandwidth', 'Free SSL', '1 Domain Included']
          }, {
            name: 'Creator Core',
            price: 79,
            desc: 'Optimized for high-traffic media portfolios.',
            features: ['50GB NVMe Storage', 'Unlimited Bandwidth', 'Dedicated IP', 'Priority Support'],
            popular: true
          }, {
            name: 'Empire Pro',
            price: 199,
            desc: 'The ultimate power for labels and elite creators.',
            features: ['250GB NVMe Storage', 'Global Edge CDN', 'White-Glove Migration', '24/7 VIP Support']
          }].map((plan, i) => <div key={i} className={`p-8 rounded-3xl border ${plan.popular ? 'bg-[#1A1A1A] border-[#FFD700] shadow-[0_0_40px_rgba(255,215,0,0.1)] relative' : 'bg-black/40 border-white/5'}`} data-id="element-403">
                {plan.popular && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#FFD700] text-black px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" data-id="element-404">
                    Most Popular
                  </div>}
                <h3 className="font-['Archivo_Black'] text-xl uppercase mb-2" data-id="element-405">
                  {plan.name}
                </h3>
                <p className="text-gray-500 text-xs mb-8 h-8" data-id="element-406">{plan.desc}</p>
                <div className="mb-8" data-id="element-407">
                  <span className="text-4xl font-['Archivo_Black']" data-id="element-408">
                    ${plan.price}
                  </span>
                  <span className="text-gray-500 text-sm font-bold uppercase tracking-widest" data-id="element-409">
                    /mo
                  </span>
                </div>
                <ul className="space-y-4 mb-10" data-id="element-410">
                  {plan.features.map((feat, j) => <li key={j} className="flex items-center gap-3 text-sm text-gray-400" data-id="element-411">
                      <CheckCircle2 size={16} className="text-[#FFD700]" data-id="element-412" />
                      {feat}
                    </li>)}
                </ul>
                <button onClick={() => startCheckout('hosting', undefined, {
              name: plan.name,
              price: plan.price
            })} className={`w-full py-4 rounded-xl font-bold uppercase tracking-widest transition-all ${plan.popular ? 'bg-[#FFD700] text-black hover:bg-white' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`} data-id="element-413">
                  Select Plan
                </button>
              </div>)}
          </div>
        </section>

        {/* Security Section */}
        <section className="mb-32 p-12 bg-[#1A1A1A] border border-white/5 rounded-3xl relative overflow-hidden" data-id="element-414">
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-415">
            <div className="lg:col-span-1" data-id="element-416">
              <h3 className="font-['Archivo_Black'] text-2xl uppercase mb-6" data-id="element-417">
                Military-Grade Security
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-8" data-id="element-418">
                Your content is your empire. We protect it with the same
                technology used by global financial institutions.
              </p>
              <div className="flex items-center gap-4 p-4 bg-black/40 rounded-xl border border-white/5" data-id="element-419">
                <Lock className="text-emerald-500" size={24} data-id="element-420" />
                <div data-id="element-421">
                  <p className="text-xs font-bold uppercase text-emerald-500" data-id="element-422">
                    SSL Certificate
                  </p>
                  <p className="text-[10px] text-gray-500" data-id="element-423">
                    Active & Encrypted
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-424">
              {[{
              title: 'DDoS Protection',
              desc: 'Advanced mitigation layers to keep you online during attacks.',
              icon: Shield
            }, {
              title: 'Daily Backups',
              desc: 'Automated snapshots of your entire site, kept for 30 days.',
              icon: RefreshCw
            }, {
              title: 'Malware Scanning',
              desc: 'Real-time monitoring for suspicious activity and code.',
              icon: Search
            }, {
              title: 'WAF Firewall',
              desc: 'Enterprise Web Application Firewall blocks malicious traffic.',
              icon: Lock
            }].map((item, i) => <div key={i} className="space-y-4" data-id="element-425">
                  <item.icon className="text-[#FFD700]" size={28} data-id="element-426" />
                  <h4 className="font-bold uppercase text-lg" data-id="element-427">{item.title}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed" data-id="element-428">
                    {item.desc}
                  </p>
                </div>)}
            </div>
          </div>
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#FFD700]/5 to-transparent pointer-events-none" data-id="element-429" />
        </section>

        {/* Global Network Section */}
        <section className="mb-32 text-center" data-id="element-430">
          <h2 className="font-['Archivo_Black'] text-3xl md:text-5xl uppercase mb-16" data-id="element-431">
            Global Edge Network
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-432">
            {[{
            region: 'North America',
            cities: ['New York', 'Los Angeles', 'Miami']
          }, {
            region: 'Europe',
            cities: ['London', 'Amsterdam', 'Frankfurt']
          }, {
            region: 'Asia Pacific',
            cities: ['Tokyo', 'Singapore', 'Sydney']
          }, {
            region: 'Latin America',
            cities: ['São Paulo', 'Mexico City']
          }].map((region, i) => <div key={i} className="space-y-4" data-id="element-433">
                <h4 className="font-bold uppercase text-[#FFD700] text-sm tracking-widest" data-id="element-434">
                  {region.region}
                </h4>
                <ul className="space-y-2" data-id="element-435">
                  {region.cities.map((city, j) => <li key={j} className="text-gray-500 text-xs flex items-center justify-center gap-2" data-id="element-436">
                      <div className="w-1 h-1 bg-white/20 rounded-full" data-id="element-437" />
                      {city}
                    </li>)}
                </ul>
              </div>)}
          </div>
          <p className="mt-16 text-sm text-gray-500 font-medium italic" data-id="element-438">
            "Content delivered from the edge, ensuring your fans never wait for
            a page to load."
          </p>
        </section>

        {/* CTA Section */}
        <section className="text-center py-20 border-t border-white/5" data-id="element-439">
          <h3 className="font-['Archivo_Black'] text-2xl md:text-4xl uppercase mb-8" data-id="element-440">
            Ready to scale?
          </h3>
          <p className="text-gray-400 mb-12 max-w-2xl mx-auto" data-id="element-441">
            Join the elite creators who trust HipHopServers for their global
            infrastructure.
          </p>
          <button onClick={() => startCheckout('search')} className="group flex items-center gap-3 bg-white text-black px-12 py-6 rounded-full font-bold uppercase tracking-widest hover:bg-[#FFD700] transition-all shadow-xl mx-auto" data-id="element-442">
            Get Started Now
            <ArrowRight className="group-hover:translate-x-2 transition-transform" data-id="element-443" />
          </button>
        </section>
      </div>

      <AnimatePresence data-id="element-444">
        {showCheckout && <CheckoutFlow initialStep={checkoutInitialStep} initialDomain={selectedDomainForCheckout} initialHosting={selectedHostingForCheckout} onClose={() => setShowCheckout(false)} data-id="element-445" />}
      </AnimatePresence>
    </div>;
}